/**
 * Checklist -> Cockpit Explorer locate matching.
 *
 * For every live checklist item, find a match: first in
 * multi_step_locates[].checklist_match, otherwise in controls[].checklist_match.
 * Match case-insensitively, ignoring punctuation and extra spaces.
 * Where a locate has phase_scope, only apply it in those phases.
 * Where a locate has match_value, only apply it when the checklist item's
 * own value (action text) contains that text (also case/punctuation
 * insensitive) - otherwise it falls back to the next candidate, exactly
 * like the item-only match used to work before match_value existed.
 */

export const normalizeMatchText = (s) => {
  if (!s) return '';
  return s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
};

// Builds (and caches, keyed by object identity) lookup indexes from the raw
// explorer dataset so repeated matching across 218 checklist items is cheap.
const indexCache = new WeakMap();

const buildIndex = (explorerData) => {
  if (indexCache.has(explorerData)) return indexCache.get(explorerData);

  const viewToZone = {};
  explorerData.zones.forEach((zone) => {
    zone.views.forEach((view) => {
      viewToZone[view.id] = zone.id;
    });
  });

  const controlById = {};
  explorerData.controls.forEach((c) => {
    controlById[c.id] = c;
  });

  // normalized checklist_match text -> first control with that text (document order)
  const controlMatchMap = new Map();
  explorerData.controls.forEach((c) => {
    (c.checklist_match || []).forEach((cm) => {
      const n = normalizeMatchText(cm);
      if (n && !controlMatchMap.has(n)) controlMatchMap.set(n, c);
    });
  });

  // normalized checklist_match text -> list of {locate, phaseScope}
  const multiMatchMap = new Map();
  (explorerData.multi_step_locates || []).forEach((locate) => {
    (locate.checklist_match || []).forEach((cm) => {
      const n = normalizeMatchText(cm);
      if (!multiMatchMap.has(n)) multiMatchMap.set(n, []);
      multiMatchMap.get(n).push(locate);
    });
  });

  const index = { viewToZone, controlById, controlMatchMap, multiMatchMap };
  indexCache.set(explorerData, index);
  return index;
};

const resolveControlLocation = (controlId, index) => {
  const control = index.controlById[controlId];
  if (!control) return null;
  return { control, zoneId: index.viewToZone[control.view], viewId: control.view };
};

/**
 * Returns null, or:
 *   { type: 'single', control, zoneId, viewId }
 *   { type: 'multi', locate, steps: [{ control, zoneId, viewId, text }, ...] }
 */
export const matchChecklistLocate = (itemText, itemValue, phaseTitle, explorerData) => {
  if (!explorerData) return null;
  const index = buildIndex(explorerData);
  const n = normalizeMatchText(itemText);
  if (!n) return null;

  const multiCandidates = index.multiMatchMap.get(n);
  if (multiCandidates) {
    const valueNorm = normalizeMatchText(itemValue);
    const locate = multiCandidates.find((m) => {
      if (m.phase_scope && !m.phase_scope.includes(phaseTitle)) return false;
      if (m.match_value) return valueNorm.includes(normalizeMatchText(m.match_value));
      return true;
    });
    if (locate) {
      const steps = locate.steps
        .map((step) => {
          const loc = resolveControlLocation(step.control, index);
          return loc ? { ...loc, text: step.text } : null;
        })
        .filter(Boolean);
      if (steps.length) return { type: 'multi', locate, steps };
    }
  }

  const control = index.controlMatchMap.get(n);
  if (control) {
    const loc = resolveControlLocation(control.id, index);
    if (loc) return { type: 'single', ...loc };
  }

  return null;
};
